// Design System Typography Tokens
// Consistent typography scale and font definitions

// "Agricultural Intelligence" Typography Strategy
export const TYPOGRAPHY_TOKENS = {
  fonts: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif', // Modern, highly legible for data-heavy interfaces
    heading: '"Poppins", "Inter", sans-serif', // Friendly, rounded for headings and marketing content
    mono: '"SF Mono", "Monaco", "Cascadia Code", monospace', // Technical data display and code
  },
  fontSizes: {
    xs: '0.75rem',   // 12px
    sm: '0.875rem',  // 14px
    base: '1rem',    // 16px
    lg: '1.125rem',  // 18px
    xl: '1.25rem',   // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem',  // 36px
    '5xl': '3rem',     // 48px
    '6xl': '3.75rem',  // 60px
    '7xl': '4.5rem',   // 72px
  },
  fontWeights: {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  lineHeights: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  // Semantic typography styles - Agricultural Intelligence Scale
  textStyles: {
    // Headings - Using Poppins for friendly, approachable feel
    h1: {
      fontFamily: '"Poppins", "Inter", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem', // 40px - Page titles
      lineHeight: 1.2, // 48px
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Poppins", "Inter", sans-serif',
      fontWeight: 600,
      fontSize: '2rem', // 32px - Section headers
      lineHeight: 1.25, // 40px
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Poppins", "Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem', // 24px - Subsections
      lineHeight: 1.33, // 32px
    },
    h4: {
      fontFamily: '"Poppins", "Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem', // 20px - Card titles
      lineHeight: 1.4, // 28px
    },
    h5: {
      fontFamily: '"Poppins", "Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.125rem', // 18px - Small headers
      lineHeight: 1.33, // 24px
    },
    h6: {
      fontFamily: '"Poppins", "Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1rem', // 16px - Labels
      lineHeight: 1.5, // 24px
    },
    // Body text - Inter for optimal readability
    bodyLarge: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '1.125rem', // 18px - Important content
      lineHeight: 1.56, // 28px
      fontWeight: 400,
    },
    body1: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '1rem', // 16px - Standard text
      lineHeight: 1.5, // 24px
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.875rem', // 14px - Secondary info
      lineHeight: 1.43, // 20px
      fontWeight: 400,
    },
    // Utility text
    caption: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.75rem', // 12px - Metadata
      lineHeight: 1.33, // 16px
      fontWeight: 400,
    },
    overline: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.75rem', // 12px
      lineHeight: 1.33, // 16px
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    // Interactive text
    button: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
    // Links
    link: {
      fontFamily: '"Inter", sans-serif',
      fontSize: '1rem', // 16px
      fontWeight: 500,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  // Responsive typography breakpoints
  // Responsive typography for optimal mobile experience
  responsive: {
    mobile: {
      h1: {
        fontSize: '2rem', // 32px on mobile
        lineHeight: 1.25,
      },
      h2: {
        fontSize: '1.5rem', // 24px on mobile
        lineHeight: 1.33,
      },
      h3: {
        fontSize: '1.25rem', // 20px on mobile
      },
      bodyLarge: {
        fontSize: '1rem', // 16px on mobile
      },
    },
    tablet: {
      h1: {
        fontSize: '2.25rem', // 36px on tablet
        lineHeight: 1.22,
      },
      h2: {
        fontSize: '1.75rem', // 28px on tablet
      },
    },
  },
} as const;

// Typography utility functions
export const getTextStyle = (style: keyof typeof TYPOGRAPHY_TOKENS.textStyles) => {
  return TYPOGRAPHY_TOKENS.textStyles[style];
};

export const getResponsiveTextStyle = (
  style: keyof typeof TYPOGRAPHY_TOKENS.textStyles,
  breakpoint?: 'mobile' | 'tablet'
) => {
  const baseStyle = TYPOGRAPHY_TOKENS.textStyles[style];
  const responsiveOverrides = breakpoint && TYPOGRAPHY_TOKENS.responsive[breakpoint] ? 
    (TYPOGRAPHY_TOKENS.responsive[breakpoint] as any)[style] || {} : {};
  
  return {
    ...baseStyle,
    ...responsiveOverrides,
  };
};